import {
  Badge,
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@nexus/ui";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdPlatformIcon } from "@/features/opportunities/components/AdPlatformIcon.js";
import { PERSONA_EXTRA_ROLLING_PRESET } from "@/features/opportunities/components/sets/CreateSetDateRangePicker/CreateSetDateRangePicker.js";
import { SetDateWindowMeta } from "@/features/opportunities/components/sets/SetDateWindowMeta/SetDateWindowMeta.js";
import { humanizeBrandName } from "@/features/opportunities/lib/format-opportunity.js";
import { adPlatformLabel } from "@/lib/ad-platform-labels.js";
import { PersonaOpportunitySelectCheckbox } from "../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";
import { PERSONA_HUB_RESULTS_CLASS } from "../../../lib/persona-opportunity-stage-chrome.js";
import type { HubChromeSet } from "./hub-chrome.types.js";
import { nextSort, type HubOpportunitySort } from "./hub-opportunity-table.js";
import { hubSetTypeLabel } from "./hub-set-type-label.js";
import { sortHubSets, type HubSetSortColumn } from "./hub-sets-table.js";
import type { OpportunityHubListState } from "./OpportunityHubListFooter.js";
import { useHubVirtualList } from "./useHubVirtualList.js";

const SET_TABLE_COLUMN_COUNT = 10;

export interface OpportunityHubSetsTableProps {
  sets: HubChromeSet[];
  selectedSetIds: ReadonlySet<string>;
  onToggleSet: (setId: string) => void;
  onToggleVisible: () => void;
  listState?: OpportunityHubListState;
}

function setStatusVariant(status: HubChromeSet["status"]) {
  if (status === "READY") return "status-connected" as const;
  if (status === "GENERATING") return "status-in-production" as const;
  return "status-error" as const;
}

function SetSortHead({
  columnId,
  label,
  sort,
  onSortChange,
  alignEnd = false,
}: {
  columnId: HubSetSortColumn;
  label: string;
  sort: HubOpportunitySort | null;
  onSortChange: (columnId: HubSetSortColumn) => void;
  alignEnd?: boolean;
}) {
  const sorted = sort?.columnId === columnId ? sort.direction : null;
  const sortIcon = sorted === "asc" ? "chevron-up" : sorted === "desc" ? "chevron-down" : "chevrons-up-down";

  return (
    <TableHead
      aria-sort={sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"}
      className={cn("group/head", alignEnd && "text-right")}
    >
      <button
        type="button"
        className={cn(
          "inline-flex max-w-full min-h-11 min-w-11 cursor-pointer select-none items-center gap-1 bg-transparent p-0 font-[inherit] uppercase tracking-wide text-inherit [@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0",
          alignEnd && "w-full justify-end text-right",
        )}
        onClick={() => onSortChange(columnId)}
      >
        <span className="truncate">{label}</span>
        <Icon
          name={sortIcon}
          size="xs"
          aria-hidden
          className={cn(
            "shrink-0 transition-opacity",
            sorted
              ? "text-primary opacity-100"
              : "text-muted-foreground/40 opacity-0 group-hover/head:opacity-60",
          )}
        />
      </button>
    </TableHead>
  );
}

export function OpportunityHubSetsTable({
  sets,
  selectedSetIds,
  onToggleSet,
  onToggleVisible,
  listState,
}: OpportunityHubSetsTableProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [sort, setSort] = useState<HubOpportunitySort | null>(null);
  const sortedSets = useMemo(() => sortHubSets(sets, sort, t), [sets, sort, t]);
  const changeSort = (columnId: HubSetSortColumn) => {
    setSort((current) => nextSort(current, columnId));
  };
  const windowed = useHubVirtualList({
    count: sortedSets.length,
    estimateSize: 40,
    scrollRef,
    hasMore: listState?.hasMore,
    isFetchingMore: listState?.isLoadingMore,
    onLoadMore: listState?.onLoadMore,
  });
  const allSelected = sortedSets.length > 0 && sortedSets.every((set) => selectedSetIds.has(set.id));
  const someSelected = sortedSets.some((set) => selectedSetIds.has(set.id)) && !allSelected;

  return (
    <div ref={scrollRef} className={PERSONA_HUB_RESULTS_CLASS}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <PersonaOpportunitySelectCheckbox
                checked={allSelected}
                onToggle={onToggleVisible}
                label={t("personaOpportunities.hub.sets.selectAllAria", "Select all visible sets")}
              />
              <span className="sr-only">
                {t("personaOpportunities.active.selectColumn", "Select")}
              </span>
              {someSelected ? (
                <span className="sr-only">
                  {t("personaOpportunities.hub.heading.setsSelected_other", {
                    count: selectedSetIds.size,
                    defaultValue: `${selectedSetIds.size} sets selected`,
                  })}
                </span>
              ) : null}
            </TableHead>
            <SetSortHead
              columnId="name"
              label={t("personaOpportunities.hub.sets.columns.name", "Name")}
              sort={sort}
              onSortChange={changeSort}
            />
            <SetSortHead
              columnId="type"
              label={t("personaOpportunities.hub.sets.columns.type", "Type")}
              sort={sort}
              onSortChange={changeSort}
            />
            <SetSortHead
              columnId="platform"
              label={t("personaOpportunities.hub.sets.columns.platform", "Platform")}
              sort={sort}
              onSortChange={changeSort}
            />
            <SetSortHead
              columnId="status"
              label={t("personaOpportunities.hub.sets.columns.status", "Status")}
              sort={sort}
              onSortChange={changeSort}
            />
            <SetSortHead
              columnId="window"
              label={t("personaOpportunities.hub.sets.columns.window", "Date window")}
              sort={sort}
              onSortChange={changeSort}
            />
            <SetSortHead
              columnId="opportunities"
              label={t("personaOpportunities.hub.sets.columns.opportunities", "Opportunities")}
              sort={sort}
              onSortChange={changeSort}
              alignEnd
            />
            <SetSortHead
              columnId="scripts"
              label={t("personaOpportunities.hub.sets.columns.scripts", "Scripts")}
              sort={sort}
              onSortChange={changeSort}
              alignEnd
            />
            <SetSortHead
              columnId="briefs"
              label={t("personaOpportunities.hub.sets.columns.briefs", "Briefs")}
              sort={sort}
              onSortChange={changeSort}
              alignEnd
            />
            <SetSortHead
              columnId="sent"
              label={t("personaOpportunities.hub.sets.columns.sent", "Sent")}
              sort={sort}
              onSortChange={changeSort}
              alignEnd
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {windowed.topPad > 0 ? (
            <TableRow aria-hidden>
              <TableCell colSpan={SET_TABLE_COLUMN_COUNT} style={{ height: windowed.topPad, padding: 0, border: 0 }} />
            </TableRow>
          ) : null}
          {windowed.indexes.map((index) => {
            const set = sortedSets[index];
            if (!set) {
              return (
                <TableRow key="sets-more" role="status">
                  <TableCell colSpan={SET_TABLE_COLUMN_COUNT}>
                    <span className="sr-only">{t("common.loading")}</span>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              );
            }
            const selected = selectedSetIds.has(set.id);
            return (
              <TableRow
                key={set.id}
                data-state={selected ? "selected" : undefined}
                className="cursor-pointer"
                onClick={() => onToggleSet(set.id)}
              >
                <TableCell>
                  <PersonaOpportunitySelectCheckbox
                    checked={selected}
                    onToggle={() => onToggleSet(set.id)}
                    label={t("personaOpportunities.hub.sets.selectSetAria", {
                      name: set.name,
                      defaultValue: `Select set ${set.name}`,
                    })}
                  />
                </TableCell>
                <TableCell className="font-medium">{set.name}</TableCell>
                <TableCell>{hubSetTypeLabel(t, set.type)}</TableCell>
                <TableCell>
                  {set.adPlatform ? (
                    <span className="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap">
                      <AdPlatformIcon platformId={set.adPlatform.toLowerCase()} />
                      <span className="truncate">
                        {adPlatformLabel(t, set.adPlatform, humanizeBrandName(set.adPlatform.toLowerCase()))}
                      </span>
                    </span>
                  ) : (
                    t("personaOpportunities.active.valueUnavailable", "—")
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={setStatusVariant(set.status)} dot>
                    {set.status === "READY"
                      ? t("personaOpportunities.hub.sets.statusReady", "Ready")
                      : set.status === "GENERATING"
                        ? t("personaOpportunities.hub.sets.statusGenerating", "Generating")
                        : t("personaOpportunities.hub.sets.statusFailed", "Failed")}
                  </Badge>
                </TableCell>
                <TableCell>
                  <SetDateWindowMeta
                    variant="table"
                    startDate={set.startDate}
                    endDate={set.endDate}
                    extraRollingPresets={[PERSONA_EXTRA_ROLLING_PRESET]}
                  />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {set.type === "USER_GENERATED"
                    ? t("personaOpportunities.active.valueUnavailable", "—")
                    : set.opportunityCount}
                </TableCell>
                <TableCell className="text-right tabular-nums text-primary">{set.scriptCount}</TableCell>
                <TableCell className="text-right tabular-nums text-primary">{set.briefCount}</TableCell>
                <TableCell className="text-right tabular-nums">{set.sentCount}</TableCell>
              </TableRow>
            );
          })}
          {windowed.bottomPad > 0 ? (
            <TableRow aria-hidden>
              <TableCell
                colSpan={SET_TABLE_COLUMN_COUNT}
                style={{ height: windowed.bottomPad, padding: 0, border: 0 }}
              />
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}
